/**
 * ET-Rechner: vergleicht die Lage zweier Felgen an derselben Radnabe.
 *
 * Port von wawi `packages/shared/src/calculators/wheel-geometry.ts` und
 * `spurweite.ts` (dort mit 13 Tests belegt). Eigenstaendige Kopie in
 * Vanilla-JS, weil das Theme keinen Bundler hat. Wer eine Formel aendert,
 * aendert sie in wawi mit.
 *
 * Geometrie (Definition der Einpresstiefe): ET ist der Abstand zwischen der
 * Felgenmittelebene und der Anlageflaeche an der Nabe. Positive ET = Anlage
 * liegt weiter aussen, das Rad sitzt weiter INNEN im Radhaus. Damit gilt fuer
 * die halbe Maulweite h = Breite(Zoll) * 25,4 / 2:
 *   Innenkante  = h + ET_eff   (Abstand Anlageflaeche -> innere Felgenkante)
 *   Aussenkante = h - ET_eff   (Abstand Anlageflaeche -> aeussere Felgenkante)
 *   ET_eff      = ET - Distanzscheibe (die Scheibe schiebt das ganze Rad nach
 *                 aussen, wirkt also wie eine kleinere ET)
 *   Spur je Achse = 2 * (ET_eff_alt - ET_eff_neu)
 *
 * Gerechnet wird mit der Maulweite (J-Mass). Das Felgenhorn kommt real auf
 * jeder Seite noch dazu, kuerzt sich beim Vergleich zweier Felgen aber
 * heraus, solange beide ein Horn haben. Reifenflanke, Sturz und Federweg sind
 * absichtlich nicht enthalten.
 */
(function () {
  'use strict';

  var ZOLL_IN_MM = 25.4;
  var GRENZEN = {
    breite: { min: 4, max: 15 },
    et: { min: -80, max: 80 },
    distanz: { min: 0, max: 60 },
  };

  /* ----------------------------------------------------------- Rechenkern */

  function inSpanne(wert, s) {
    return Number.isFinite(wert) && wert >= s.min && wert <= s.max;
  }

  function istGueltig(f) {
    return inSpanne(f.breite, GRENZEN.breite) && inSpanne(f.et, GRENZEN.et) && inSpanne(f.distanz || 0, GRENZEN.distanz);
  }

  /** Lage einer Felge: effektive ET, Innen- und Aussenkante ab Anlageflaeche (mm). */
  function lage(f) {
    var halb = (f.breite * ZOLL_IN_MM) / 2;
    var etEffektiv = f.et - (f.distanz || 0);
    return {
      breiteMm: f.breite * ZOLL_IN_MM,
      etEffektiv: etEffektiv,
      innen: halb + etEffektiv,
      aussen: halb - etEffektiv,
    };
  }

  /** Spurweitenaenderung: Δ je Seite = (ET_alt - ET_neu) + Distanz. */
  function spur(etAlt, etNeu, distanz) {
    var jeSeite = etAlt - etNeu + (distanz || 0);
    return { jeSeite: jeSeite, jeAchse: jeSeite * 2 };
  }

  function vergleich(alt, neu) {
    var a = lage(alt);
    var b = lage(neu);
    var halbNeu = (neu.breite * ZOLL_IN_MM) / 2;
    var distNeu = neu.distanz || 0;
    return {
      alt: a,
      neu: b,
      innenMm: b.innen - a.innen,     // positiv: reicht weiter nach innen, weniger Platz
      aussenMm: b.aussen - a.aussen,  // positiv: steht weiter nach aussen
      spurSeiteMm: a.etEffektiv - b.etEffektiv,
      spurAchseMm: 2 * (a.etEffektiv - b.etEffektiv),
      etGleicheInnen: a.innen - halbNeu + distNeu,
      etGleicheAussen: halbNeu - a.aussen + distNeu,
    };
  }

  /* -------------------------------------------------------------- Bewertung */

  /**
   * Einordnung nach der groesseren Kantenverschiebung. Eigene Schwellen als
   * Orientierung, keine Rechtsaussage: was zulaessig ist, steht in der
   * Freigabe des Rades und entscheidet die Pruefstelle.
   */
  function einordnen(maxKanteMm) {
    var b = Math.abs(maxKanteMm);
    if (b <= 3) {
      return {
        stufe: 'gut',
        titel: 'Nahezu gleiche Lage im Radhaus',
        text: 'Die Felgenkanten wandern um höchstens drei Millimeter. Optisch und beim Freigang ändert sich praktisch nichts. Ob die Kombination an Ihrem Fahrzeug zulässig ist, klären Freigabe des Rades, Fahrzeugpapiere und Prüfstelle.',
      };
    }
    if (b <= 10) {
      return {
        stufe: 'mittel',
        titel: 'Spürbare Verschiebung',
        text: 'Eine Felgenkante wandert um bis zu zehn Millimeter. Prüfen Sie den Freigang zu Kotflügel, Federbein und Bremse bei vollem Einschlag und eingefedert. Ob die Kombination zulässig ist, klären Sie vor dem Kauf mit der Prüfstelle und anhand der Freigabe des Rades.',
      };
    }
    return {
      stufe: 'hoch',
      titel: 'Deutliche Verschiebung',
      text: 'Mehr als zehn Millimeter an einer Felgenkante verändern Freigang, Optik und Lenkverhalten sichtbar. Diese Kombination gehört vor Kauf und Montage mit der Prüfstelle abgestimmt und gegen die Freigabe des Rades und die Fahrzeugpapiere geprüft.',
    };
  }

  /* ------------------------------------------------------------- Darstellung */

  function zahl(wert, stellen) {
    return wert.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
  }

  function mitVorzeichen(wert, stellen) {
    var gerundet = Math.round(wert * Math.pow(10, stellen)) / Math.pow(10, stellen);
    return (gerundet > 0 ? '+' : gerundet < 0 ? '−' : '') + zahl(Math.abs(gerundet), stellen);
  }

  function breiteText(f) {
    return zahl(f.breite, f.breite % 1 ? 1 : 0) + 'J ET' + zahl(f.et, 0) + (f.distanz ? ' + ' + zahl(f.distanz, 0) + ' mm' : '');
  }

  /* ------------------------------------------------------------------ Widget */

  function init(wurzel) {
    var felder = {
      altBreite: wurzel.querySelector('[data-er="alt-breite"]'),
      altEt: wurzel.querySelector('[data-er="alt-et"]'),
      neuBreite: wurzel.querySelector('[data-er="neu-breite"]'),
      neuEt: wurzel.querySelector('[data-er="neu-et"]'),
      neuDistanz: wurzel.querySelector('[data-er="neu-distanz"]'),
    };
    var ausgabe = wurzel.querySelector('[data-er="ausgabe"]');
    var hinweis = wurzel.querySelector('[data-er="hinweis"]');
    if (!ausgabe) return;

    function lies(praefix) {
      var f = {
        breite: parseFloat(felder[praefix + 'Breite'].value),
        et: parseFloat(felder[praefix + 'Et'].value),
        distanz: praefix === 'neu' ? parseFloat(felder.neuDistanz.value) || 0 : 0,
      };
      return istGueltig(f) ? f : null;
    }

    function schreib(praefix, f) {
      felder[praefix + 'Breite'].value = f.breite;
      felder[praefix + 'Et'].value = f.et;
      if (praefix === 'neu') felder.neuDistanz.value = f.distanz || 0;
    }

    function setzen(name, text) {
      var el = wurzel.querySelector('[data-er="' + name + '"]');
      if (el) el.textContent = text;
    }

    function rechne() {
      var alt = lies('alt');
      var neu = lies('neu');
      if (!alt || !neu) {
        ausgabe.hidden = true;
        if (hinweis) {
          hinweis.hidden = false;
          hinweis.textContent =
            'Bitte beide Felgen vollständig eintragen. Breite ' + GRENZEN.breite.min + ' bis ' + GRENZEN.breite.max +
            ' Zoll, ET ' + GRENZEN.et.min + ' bis ' + GRENZEN.et.max + ' mm, Distanzscheibe 0 bis ' + GRENZEN.distanz.max + ' mm je Seite.';
        }
        return;
      }
      if (hinweis) hinweis.hidden = true;

      var v = vergleich(alt, neu);
      var maxKante = Math.abs(v.aussenMm) >= Math.abs(v.innenMm) ? v.aussenMm : v.innenMm;
      var urteil = einordnen(maxKante);

      setzen('urteil-titel', urteil.titel);
      setzen('urteil-text', urteil.text);
      var karte = wurzel.querySelector('[data-er="urteil"]');
      if (karte) karte.setAttribute('data-stufe', urteil.stufe);

      setzen('wert-felge-alt', breiteText(alt));
      setzen('wert-felge-neu', breiteText(neu));

      setzen('wert-spur-seite', mitVorzeichen(v.spurSeiteMm, 1) + ' mm');
      setzen('wert-spur-seite-zusatz', v.spurSeiteMm > 0 ? 'Rad wandert nach außen' : v.spurSeiteMm < 0 ? 'Rad wandert nach innen' : 'Radmitte bleibt');
      setzen('wert-spur-achse', mitVorzeichen(v.spurAchseMm, 1) + ' mm');
      setzen('wert-aussen', mitVorzeichen(v.aussenMm, 1) + ' mm');
      setzen('wert-aussen-zusatz', v.aussenMm > 0 ? 'weiter Richtung Kotflügel' : v.aussenMm < 0 ? 'weiter vom Kotflügel weg' : 'unverändert');
      setzen('wert-innen', mitVorzeichen(v.innenMm, 1) + ' mm');
      setzen('wert-innen-zusatz', v.innenMm > 0 ? 'näher an Federbein und Bremse' : v.innenMm < 0 ? 'mehr Platz zu Federbein und Bremse' : 'unverändert');
      setzen('wert-breite', zahl(v.neu.breiteMm, 1) + ' mm');
      setzen('wert-breite-alt', zahl(v.alt.breiteMm, 1) + ' mm');

      setzen('wert-et-aussen', 'ET' + zahl(v.etGleicheAussen, 1));
      setzen('wert-et-innen', 'ET' + zahl(v.etGleicheInnen, 1));

      // Funnel: Wer nach aussen geht, sucht meist Spurverbreiterungen; wer
      // die Lage haelt oder nach innen geht, braucht die passende Felge.
      var linkA = wurzel.querySelector('[data-er="link-primaer"]');
      if (linkA) {
        if (v.spurSeiteMm > 0) {
          linkA.href = '/collections/spurverbreiterung';
          linkA.textContent = 'Spurverbreiterungen ansehen';
        } else {
          linkA.href = '/pages/felgen-konfigurator';
          linkA.textContent = 'Felgen für Ihr Fahrzeug finden';
        }
      }

      ausgabe.hidden = false;
    }

    Object.keys(felder).forEach(function (k) {
      if (felder[k]) felder[k].addEventListener('input', rechne);
    });

    wurzel.querySelectorAll('[data-er-beispiel]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var a = btn.getAttribute('data-alt').split(',');
        var n = btn.getAttribute('data-neu').split(',');
        schreib('alt', { breite: +a[0], et: +a[1] });
        schreib('neu', { breite: +n[0], et: +n[1], distanz: +(n[2] || 0) });
        rechne();
        ausgabe.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });

    rechne();
  }

  function start() {
    document.querySelectorAll('[data-zr-etrechner]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.zrEtRechner = { lage: lage, vergleich: vergleich, spur: spur, einordnen: einordnen };
})();
